import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useTemplateScopeMutation } from '../../../../queries';
import { useUpdateTemplateUserGroupsMutation } from '../../../../queries/Templates/useUpdateTemplateUserGroupsMutation';
import { useLoadAllUserGroups } from '../../../../queries/UserGroup/useLoadAllUserGroups';
import { ITemplateCard } from '../../../../viewmodels/AppViewModels';
import { Button } from '../../../components-v2/Button';
import { Checkbox } from '../../../components-v2/Checkbox';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { grayIconFill } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { LoadingSpinner } from '../../LoadingSpinner';
import { Toggle } from '../../Toggle';
import { styleSheet } from './styles';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	template: ITemplateCard | undefined | null;
}

export const TemplateUserGroupsDialog: React.FC<Props> = ({ isOpen, onClose, onSuccess, template }) => {
	const [isShared, setIsShared] = React.useState(false);
	const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

	const { userGroups, isLoading } = useLoadAllUserGroups({ enabled: isOpen && isShared });

	const scopeMutation = useTemplateScopeMutation();
	const userGroupsMutation = useUpdateTemplateUserGroupsMutation();

	const isPending = scopeMutation.isPending || userGroupsMutation.isPending;

	React.useEffect(() => {
		if (isOpen) {
			setIsShared(template?.scope === Api.TemplateScope.Account);
			const userGroupIds = template?.userGroupIds ?? [];
			setSelectedIds(userGroupIds.includes('all') ? [] : userGroupIds);
		}
	}, [isOpen, template?.scope, template?.userGroupIds]);

	const handleToggle = (groupId: string, checked: boolean | 'indeterminate') => {
		if (checked === true) {
			setSelectedIds(prev => [...prev, groupId]);
		} else {
			setSelectedIds(prev => prev.filter(id => id !== groupId));
		}
	};

	const handleSelectAll = (checked: boolean | 'indeterminate') => {
		if (checked === true) {
			setSelectedIds([]);
		}
	};

	const handleSave = async () => {
		if (!template?.id) {
			return;
		}

		const originalScope = template.scope;
		try {
			await scopeMutation.mutateAsync({
				id: template.id,
				scope: isShared ? Api.TemplateScope.Account : Api.TemplateScope.User,
			});
			await userGroupsMutation.mutateAsync({
				templateId: template.id,
				userGroupIds: isShared ? selectedIds : [],
			});
			toast.success('Template updated');
			onSuccess?.();
			onClose();
		} catch {
			toast.error('Failed to update template');
			await scopeMutation.mutateAsync({
				id: template.id,
				scope: originalScope,
			});
		}
	};

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			onClose();
		}
	};

	const customUserGroups = userGroups?.filter(g => !g.isPredefined);

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogPopup styles={[styleSheet.popup]}>
				<DialogHeader>
					<DialogTitle>{template?.name ?? 'Template Permissions'}</DialogTitle>
				</DialogHeader>
				<DialogBody styles={[styleSheet.body]}>
					<Toggle
						id='visibility-toggle'
						isOn={isShared}
						text='Share this template with other users on the account'
						disabled={isPending}
						onToggleCheckChanged={value => setIsShared(value)}
						uncheckedColor={grayIconFill}
						aria-describedby='Template visibility'
					/>
					{isShared ? (
						<div className={css(styleSheet.groupsSection)}>
							<p className={css(styleSheet.sectionLabel)}>Restrict to user groups</p>
							{isLoading ? (
								<div className={css(styleSheet.centered)}>
									<LoadingSpinner type='small' />
								</div>
							) : !customUserGroups?.length ? (
								<p className={css(styleSheet.emptyText)}>Failed to load user groups.</p>
							) : (
								<ul className={css(styleSheet.list)}>
									<li className={css(styleSheet.row)}>
										<Checkbox
											id='ug-all'
											checked={selectedIds.length === 0}
											onCheckedChange={handleSelectAll}
											disabled={isPending}
										/>
										<label htmlFor='ug-all' className={css(styleSheet.label)}>
											All users
										</label>
									</li>
									{customUserGroups.map(group => (
										<li key={group.id} className={css(styleSheet.row)}>
											<Checkbox
												id={`ug-${group.id}`}
												checked={selectedIds.includes(group.id ?? '')}
												onCheckedChange={checked => handleToggle(group.id ?? '', checked)}
												disabled={isPending}
											/>
											<label htmlFor={`ug-${group.id}`} className={css(styleSheet.label)}>
												{group.name}
											</label>
										</li>
									))}
								</ul>
							)}
						</div>
					) : null}
				</DialogBody>
				<DialogFooter>
					<Button variant='secondary' size='sm' onClick={onClose} disabled={isPending}>
						Cancel
					</Button>
					<Button variant='primary' size='sm' onClick={handleSave} disabled={isPending} styles={[bs.ml2]}>
						{isPending ? 'Saving...' : 'Save'}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
